/** Product copy of the login page; every visible string comes from one of these dictionaries. */
export interface LoginMessages {
  /** BCP 47 tag written to `<html lang>`. */
  readonly lang: string
  readonly documentTitle: string
  readonly brand: string
  readonly networkStatus: string
  readonly heroTitle: string
  readonly heroLead: string
  readonly kpiSignals: string
  readonly kpiCapabilities: string
  readonly kpiPendingTasks: string
  readonly cardTitle: string
  readonly cardSubtitle: string
  readonly username: string
  readonly password: string
  readonly showPassword: string
  readonly hidePassword: string
  readonly remember: string
  readonly forgotPassword: string
  readonly forgotPasswordHint: string
  readonly submit: string
  readonly submitting: string
  readonly or: string
  readonly ssoDefaultLabel: string
  readonly ssoExchanging: string
  readonly note: string
  readonly footer: string
  readonly missingCredentials: string
  readonly loginFailed: string
  readonly backendUnavailable: string
}

/** Simplified Chinese copy; the default dictionary. */
export const zh: LoginMessages = {
  lang: 'zh-CN',
  documentTitle: '登录 · Sinocst AI',
  brand: 'SinoCST',
  networkStatus: '全球业务智能网络',
  heroTitle: 'Sinocst AI',
  heroLead: '让 AI 深入企业业务，让全球经营更智能。四类 Agent 协同感知业务信号、分析经营风险、编排 ERP 能力，并在授权范围内推动任务执行。',
  kpiSignals: '业务信号',
  kpiCapabilities: '关联 ERP 能力',
  kpiPendingTasks: '待确认任务',
  cardTitle: '登录控制台',
  cardSubtitle: '进入企业智能业务平台',
  username: '账号',
  password: '密码',
  showPassword: '显示密码',
  hidePassword: '隐藏密码',
  remember: '记住账号',
  forgotPassword: '忘记密码',
  forgotPasswordHint: '请联系企业管理员重置密码',
  submit: '登录',
  submitting: '正在登录…',
  or: '或',
  ssoDefaultLabel: '使用企业 SSO 登录',
  ssoExchanging: '正在使用企业身份登录…',
  note: '安全连接 · 智能协同 · 全球经营',
  footer: '© 2026 SinoCST · SC AI ERP',
  missingCredentials: '请输入账号和密码',
  loginFailed: '登录失败，请重试',
  backendUnavailable: '认证服务暂时不可用，请稍后重试',
}

/** English copy for browsers whose preferred language is English. */
export const en: LoginMessages = {
  lang: 'en',
  documentTitle: 'Sign in · Sinocst AI',
  brand: 'SinoCST',
  networkStatus: 'Global business intelligence network',
  heroTitle: 'Sinocst AI',
  heroLead: 'AI that works inside your business and makes global operations smarter. Four kinds of agents sense business signals, analyze operating risk, orchestrate ERP capabilities, and carry tasks forward within the access you grant.',
  kpiSignals: 'Business signals',
  kpiCapabilities: 'Linked ERP capabilities',
  kpiPendingTasks: 'Tasks to confirm',
  cardTitle: 'Sign in to the console',
  cardSubtitle: 'Enter the enterprise intelligent business platform',
  username: 'Account',
  password: 'Password',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  remember: 'Remember account',
  forgotPassword: 'Forgot password',
  forgotPasswordHint: 'Ask your enterprise administrator to reset your password.',
  submit: 'Sign in',
  submitting: 'Signing in…',
  or: 'or',
  ssoDefaultLabel: 'Sign in with enterprise SSO',
  ssoExchanging: 'Signing in with your enterprise identity…',
  note: 'Secure connection · Intelligent collaboration · Global operations',
  footer: '© 2026 SinoCST · SC AI ERP',
  missingCredentials: 'Enter your account and password.',
  loginFailed: 'Sign-in failed. Try again.',
  backendUnavailable: 'The sign-in service is temporarily unavailable. Try again later.',
}

/**
 * Select the dictionary for a browser language tag.
 * @param language - `navigator.language`, e.g. `zh-CN` or `en-US`.
 * @returns {@link en} for English tags, otherwise {@link zh}.
 */
export function pickMessages(language: string): LoginMessages {
  return /^en(?:-|$)/i.test(language) ? en : zh
}
